import { GooglePaySubmitFn } from '@features/GooglePay/GooglePayButton/GooglePayButton.static';
import { AdyenManageCreditCardsProps } from '@features/ManageSavedCreditCards/AdyenManageCreditCards/AdyenManageCreditCards';
import { BancontactAdyenManageCreditCardsProps } from '@features/ManageSavedCreditCards/BancontactAdyenManageCreditCards/BancontactAdyenManageCreditCards';
import { CreditCardData, Environment, Nullable } from '@types-internal';
import {
  ParsedAdyenManageCreditCardsProps,
  ParsedBancontactAdyenManageCreditCardsProps,
  ParsedCoreProvidersProps,
  ParseGooglePayButtonWrapperProps,
} from '@types-internal/ExtractHtmlTypes';

import {
  checkMyAccountSetting,
  getNullKeys,
  nonEmptyString,
  parseAdyenCreditCardData,
  parseBancontactAdyenCreditCardData,
  parseLocale,
  parseShopBundle,
  parseAmount,
} from './extractIndividualDataFromHtml';
import { convertRunningEnvToAdyenEnv, safeStringify } from './utils';

function parseCoreProvidersProps(
  container: HTMLElement,
  runningEnv: Environment,
): ParsedCoreProvidersProps {
  return {
    shopBundle: parseShopBundle(container),
    domain: nonEmptyString(container, 'domain'),
    locale: parseLocale(container),
    errorContainerId: nonEmptyString(container, 'errorContainerId'),
    csrfToken: container.dataset.csrfToken,
    env: runningEnv,
  };
}

/*
 * Shared parsing/normalizing logic for the Adyen and Bancontact Adyen "manage credit cards"
 * components: they only differ in which credit-card data parser they use and their error label.
 */
function parseManageCreditCardsProps(
  container: HTMLElement,
  runningEnv: Environment,
  parseCreditCards: (container: HTMLElement) => Nullable<CreditCardData[]>,
): ParsedAdyenManageCreditCardsProps {
  return {
    ...parseCoreProvidersProps(container, runningEnv),
    containerType: checkMyAccountSetting(container),
    isFallbackPsp: container.dataset.fallbackPsp === 'true',
    creditCards: parseCreditCards(container),
  };
}

function normalizeManageCreditCardsProps<
  T extends AdyenManageCreditCardsProps | BancontactAdyenManageCreditCardsProps,
>(props: ParsedAdyenManageCreditCardsProps, container: HTMLElement, componentName: string): T {
  const { errorContainerId, shopBundle, domain, locale, creditCards } = props;

  const requiredValues = [errorContainerId, shopBundle, domain, locale, creditCards];
  const hasNullRequiredValue = requiredValues.includes(null);

  if (hasNullRequiredValue) {
    throw new Error(
      `[${componentName}] Got null keys:${safeStringify(
        getNullKeys(props),
      )} in the container dataset: ${safeStringify(container.dataset)}`,
    );
  }
  return {
    errorContainerId,
    shopBundle,
    domain,
    locale,
    creditCards,
    containerType: props.containerType,
    csrfToken: props.csrfToken,
    isFallbackPsp: props.isFallbackPsp,
    env: props.env,
  } as T;
}

/*
 * Parsing to get the props of the AdyenManageCreditCards.
 */
export function parseAdyenManageCreditCardsProps(
  container: HTMLElement,
  runningEnv: Environment,
): ParsedAdyenManageCreditCardsProps {
  return parseManageCreditCardsProps(container, runningEnv, parseAdyenCreditCardData);
}

export function normalizeAdyenManageCreditCardsProps(
  container: HTMLElement,
  runningEnv: Environment,
): AdyenManageCreditCardsProps {
  const props = parseAdyenManageCreditCardsProps(container, runningEnv);
  return normalizeManageCreditCardsProps<AdyenManageCreditCardsProps>(
    props,
    container,
    'AdyenManageCreditCards',
  );
}

/**
 * Function to parse the props of the BancontactAdyenManageCreditCards.
 *
 * @param container HTMLElement container where the props are stored
 * @param runningEnv Environment where the application is running
 * @returns ParsedBancontactAdyenManageCreditCardsProps to be used in the component
 */
export function parseBancontactAdyenManageCreditCardsProps(
  container: HTMLElement,
  runningEnv: Environment,
): ParsedBancontactAdyenManageCreditCardsProps {
  return parseManageCreditCardsProps(container, runningEnv, parseBancontactAdyenCreditCardData);
}

export function normalizeBancontactAdyenManageCreditCardsProps(
  container: HTMLElement,
  runningEnv: Environment,
): BancontactAdyenManageCreditCardsProps {
  const props = parseBancontactAdyenManageCreditCardsProps(container, runningEnv);
  return normalizeManageCreditCardsProps<BancontactAdyenManageCreditCardsProps>(
    props,
    container,
    'BancontactAdyenManageCreditCards',
  );
}

/**
 * GooglePayButton
 */
export function parseGooglePayButtonWrapperProps(
  container: HTMLElement,
  runningEnv: Environment,
  maybePaymentMethodsResponse: unknown,
  maybeOnSubmit: unknown,
  maybeOnError: unknown,
): ParseGooglePayButtonWrapperProps {
  let onSubmit = null;
  let onError = null;
  if (typeof maybeOnSubmit === 'function') {
    onSubmit = maybeOnSubmit as GooglePaySubmitFn;
  }
  if (typeof maybeOnError === 'function') {
    onError = maybeOnError as (errorType: Nullable<string>) => void;
  }

  return {
    ...parseCoreProvidersProps(container, runningEnv),
    onError,
    onSubmit,
    adyenEnvironment: convertRunningEnvToAdyenEnv(runningEnv),
    countryCode: nonEmptyString(container, 'countryCode'),
    currency: nonEmptyString(container, 'currency'),
    amount: parseAmount(container),
    paymentMethodsResponse: maybePaymentMethodsResponse,
  };
}
